<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'ViewSpecialApprovals' }" class="mb-0 main-identifier fw-semibold text-decoration-none"
        title="Go back to Special Approvals">
        Special Approvals
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Special Approval</p>
    </div>

    <div class="d-flex justify-content-center mb-5">
      <div class="form-outer">
        <div class="crud-form position-relative">
          <div class="form-inner mb-2">
            <div class="form-header d-flex justify-content-between align-items-center">
              <h1 class="mb-0">Special Approval</h1>
              <span v-if="isDataLoaded" :class="['status-badge', statusClass]">{{ statusLabel }}</span>
            </div>

            <div class="memo-content-area position-relative">
              <div v-if="!isDataLoaded" class="loading-overlay text-center">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading special approval details...</p>
              </div>

              <div v-if="isDataLoaded">
                <div class="form-body pt-3">

                <!-- 2-column field grid -->
                <div class="row g-3 mb-4">

                  <div class="col-md-6">
                    <label class="form-label">Budget Year</label>
                    <select v-if="canEditReturned" class="form-select" v-model="form.budgetYear">
                      <option value="" disabled>- Select Year -</option>
                      <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
                    </select>
                    <input v-else type="text" class="form-control form-view-only-input" :value="form.budgetYear || 'N/A'" readonly />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Nature of Expenditure</label>
                    <select v-if="canEditReturned" class="form-select" v-model="form.natOfExpenditure">
                      <option value="" disabled>- Select Type -</option>
                      <option value="3">Capital</option>
                      <option value="2">Recurrent</option>
                      <option value="4">Hybrid</option>
                    </select>
                    <input v-else type="text" class="form-control form-view-only-input"
                      :value="form.natOfExpenditure === '2' || form.natOfExpenditure === 2 ? 'Recurrent' : form.natOfExpenditure === '3' || form.natOfExpenditure === 3 ? 'Capital' : form.natOfExpenditure === '4' || form.natOfExpenditure === 4 ? 'Hybrid' : form.natOfExpenditure || 'N/A'"
                      readonly />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Approval Date</label>
                    <input v-if="canEditReturned" type="date" class="form-control" v-model="form.fileApprovedDate" />
                    <input v-else type="text" class="form-control form-view-only-input" :value="formatDate(form.fileApprovedDate)" readonly />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">File Number</label>
                    <input v-if="canEditReturned" type="text" class="form-control" v-model="form.fileNumber" />
                    <input v-else type="text" class="form-control form-view-only-input" :value="form.fileNumber || 'N/A'" readonly />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Creator Name</label>
                    <input type="text" class="form-control form-view-only-input" :value="form.initiatorName || 'N/A'" readonly />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Approver Name</label>
                    <input type="text" class="form-control form-view-only-input"
                      :value="form.approvedByCBP ? (form.commissionerName || 'N/A') : form.approvedByPermSec ? (form.permSecName || 'N/A') : (form.sectoralHeadName || 'N/A')"
                      readonly />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Purpose of Request</label>
                    <input v-if="canEditReturned" type="text" class="form-control" v-model="form.purposeOfRequest" />
                    <input v-else type="text" class="form-control form-view-only-input" :value="form.purposeOfRequest || 'N/A'" readonly />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Amount Required</label>
                    <input v-if="canEditReturned" type="text" class="form-control"
                      :value="formatAmount(form.amtRequired)"
                      @input="(e) => onAmountInput(e, form, 'amtRequired')"
                      placeholder="0.00" />
                    <input v-else type="text" class="form-control form-view-only-input" :value="formatCurrency(form.amtRequired)" readonly />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Amount Approved</label>
                    <input v-if="canEditReturned" type="text" class="form-control"
                      :value="formatAmount(form.amtApproved)"
                      @input="(e) => onAmountInput(e, form, 'amtApproved')"
                      placeholder="0.00" />
                    <input v-else type="text" class="form-control form-view-only-input" :value="formatCurrency(form.amtApproved)" readonly />
                  </div>

                  <div v-if="form.specApprovalTypeId" class="col-md-6">
                    <label class="form-label">Special Approval Type</label>
                    <input type="text" class="form-control form-view-only-input" :value="specApprovalTypeName || '...'" readonly />
                  </div>

                  <div v-if="form.returnReason" class="col-md-12">
                    <label class="form-label">Return Reason</label>
                    <textarea class="form-control form-view-only-input" rows="3" :value="form.returnReason" readonly></textarea>
                  </div>

                </div>

                <!-- Eco Line Items -->
                <template v-if="!form.parentApprovalInd && !form.parentApproval">
                <h5 class="section-title">Eco Line Items</h5>

                <div v-if="canEditReturned" class="eco-picker-box mt-3 mb-3">
                  <p class="fw-semibold mb-2" style="font-size: 0.85rem;">Select Eco Line Item to Add:</p>
                  <div v-if="ecoLoading" class="text-center py-3">
                    <div class="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></div>
                    <span class="ms-2 text-muted small">Loading eco options...</span>
                  </div>
                  <div v-else class="d-flex align-items-end gap-2 flex-wrap">
                    <div class="d-flex flex-column" style="max-width: 160px;">
                      <label class="form-label mb-1 text-muted" style="font-size: 13px;">Filter Options</label>
                      <input type="text" v-model="searchTerm" placeholder="Search title/code" class="form-control form-control-sm" />
                    </div>
                    <div class="d-flex flex-column" style="max-width: 220px;">
                      <label class="form-label mb-1 text-muted" style="font-size: 13px;">Choose Eco Line Item</label>
                      <select class="form-select form-select-sm" v-model="newEcoId" @change="onSelectEco">
                        <option value="" disabled>-- pick code --</option>
                        <option v-for="opt in filteredEcoOptions" :key="opt.id" :value="opt.id">
                          {{ opt.ecoCode }} — {{ opt.title }}
                        </option>
                      </select>
                    </div>
                    <div class="d-flex flex-column">
                      <label class="invisible mb-1">Add</label>
                      <button type="button" class="btn btn-success btn-sm" :disabled="!newEcoId" @click="addEcoItem">
                        + Add Item
                      </button>
                    </div>
                  </div>
                </div>

                <div class="table-responsive mt-3 mb-4">
                  <table class="table table-bordered text-center align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Eco Code</th>
                        <th>Title</th>
                        <th>Approved Budget (₦)</th>
                        <th>Released To Date (₦)</th>
                        <th>Balance (₦)</th>
                        <th>Amount Required (₦)</th>
                        <th>Amount Approved (₦)</th>
                        <th v-if="canEditReturned"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="!form.lineItems.length">
                        <td :colspan="canEditReturned ? 8 : 7" class="text-muted fst-italic py-3">No eco line items.</td>
                      </tr>
                      <tr v-for="(item, index) in form.lineItems" :key="item.id || index"
                        :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                        <td>{{ item.ecoCode }}</td>
                        <td>{{ item.title }}</td>
                        <td>{{ formatCurrency(item.amountBudgeted) }}</td>
                        <td>{{ formatCurrency(item.amountReleased) }}</td>
                        <td>{{ formatCurrency(item.balance) }}</td>
                        <td>
                          <input v-if="canEditReturned" type="text" class="form-control text-center"
                            :value="formatAmount(item.amountRequired)"
                            @input="(e) => onEcoAmountInput(e, item, 'amountRequired')"
                            placeholder="0.00" />
                          <span v-else>{{ formatCurrency(item.amountRequired) }}</span>
                        </td>
                        <td>
                          <input v-if="canEditReturned" type="text" class="form-control text-center"
                            :value="formatAmount(item.amountApproved)"
                            @input="(e) => onEcoAmountInput(e, item, 'amountApproved')"
                            placeholder="0.00" />
                          <span v-else>{{ formatCurrency(item.amountApproved) }}</span>
                        </td>
                        <td v-if="canEditReturned">
                          <button type="button" class="btn btn-outline-danger btn-sm" @click="removeLineItem(index)">
                            <span class="iconify" data-icon="mdi:trash-can-outline"></span>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                </template>

                <!-- Sub-approvals -->
                <template v-if="form.subApprovals && form.subApprovals.length">
                  <h5 class="section-title">Sub-special Approvals</h5>
                  <div v-for="(sub, index) in form.subApprovals" :key="sub.id || index" class="sub-approval-card mb-4">
                    <h5 class="section-title mb-3">{{ index === 0 ? 'Sub-special Approval' : `Sub-special Approval ${index + 1}` }}</h5>

                    <div class="row g-3 mb-3">
                      <div class="col-md-6">
                        <label class="form-label">Special Approval Type</label>
                        <input type="text" class="form-control form-view-only-input"
                          :value="sub.specApprovalTypeName || 'N/A'" readonly />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label">Amount Required</label>
                        <input type="text" class="form-control form-view-only-input"
                          :value="formatCurrency(sub.amtRequired)" readonly />
                      </div>
                    </div>

                    <div v-if="canEditReturned" class="eco-picker-box mb-3">
                      <p class="fw-semibold mb-2" style="font-size: 0.85rem;">Select Eco Line Item to Add:</p>
                      <div v-if="ecoLoading" class="d-flex align-items-center gap-2 text-muted small">
                        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        <span>Loading eco options...</span>
                      </div>
                      <div v-else class="d-flex align-items-end gap-2 flex-wrap">
                        <div class="d-flex flex-column" style="max-width: 160px;">
                          <label class="form-label mb-1 text-muted" style="font-size: 13px;">Filter Options</label>
                          <input type="text" v-model="sub._searchTerm" placeholder="Search title/code" class="form-control form-control-sm" />
                        </div>
                        <div class="d-flex flex-column" style="max-width: 220px;">
                          <label class="form-label mb-1 text-muted" style="font-size: 13px;">Choose Eco Line Item</label>
                          <select class="form-select form-select-sm" v-model="sub._newEcoId">
                            <option value="" disabled>-- pick code --</option>
                            <option v-for="opt in getFilteredSubEcoOptions(sub)" :key="opt.id" :value="opt.id">
                              {{ opt.ecoCode }} — {{ opt.title }}
                            </option>
                          </select>
                        </div>
                        <div class="d-flex flex-column">
                          <label class="invisible mb-1">Add</label>
                          <button type="button" class="btn btn-success btn-sm" :disabled="!sub._newEcoId" @click="addSubEcoItem(sub)">
                            + Add Item
                          </button>
                        </div>
                      </div>
                    </div>

                    <div class="table-responsive mt-2">
                      <table class="table table-bordered text-center align-middle">
                        <thead class="table-light">
                          <tr>
                            <th>Eco Code</th>
                            <th>Title</th>
                            <th>Approved Budget (₦)</th>
                            <th>Released To Date (₦)</th>
                            <th>Balance (₦)</th>
                            <th>Amount Required (₦)</th>
                            <th>Amount Approved (₦)</th>
                            <th v-if="canEditReturned"></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-if="!sub.lineItems || !sub.lineItems.length">
                            <td :colspan="canEditReturned ? 8 : 7" class="text-muted fst-italic py-3">No eco line items.</td>
                          </tr>
                          <tr v-for="(item, i) in sub.lineItems" :key="item.id || i"
                            :class="i % 2 === 0 ? 'row-even' : 'row-odd'">
                            <td>{{ item.ecoCode }}</td>
                            <td>{{ item.title }}</td>
                            <td>{{ formatCurrency(item.amountBudgeted) }}</td>
                            <td>{{ formatCurrency(item.amountReleased) }}</td>
                            <td>{{ formatCurrency(item.balance) }}</td>
                            <td>
                              <input v-if="canEditReturned" type="text" class="form-control text-center"
                                :value="formatAmount(item.amountRequired)"
                                @input="(e) => onEcoAmountInput(e, item, 'amountRequired')"
                                placeholder="0.00" />
                              <span v-else>{{ formatCurrency(item.amountRequired) }}</span>
                            </td>
                            <td>
                              <input v-if="canEditReturned" type="text" class="form-control text-center"
                                :value="formatAmount(item.amountApproved)"
                                @input="(e) => onEcoAmountInput(e, item, 'amountApproved')"
                                placeholder="0.00" />
                              <span v-else>{{ formatCurrency(item.amountApproved) }}</span>
                            </td>
                            <td v-if="canEditReturned">
                              <button type="button" class="btn btn-outline-danger btn-sm" @click="removeSubLineItem(sub, i)">
                                <span class="iconify" data-icon="mdi:trash-can-outline"></span>
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </template>

                <!-- Approval Document -->
                <div v-if="hasApprovalDocument && !isGroupMember && !documentNotFound" class="mb-4">
                  <h5 class="section-title">Approval Document</h5>
                  <div v-if="loadingDocument" class="text-muted small py-2">Loading document...</div>
                  <div v-else-if="approvalDocumentUrl" class="approval-document-wrapper">
                    <div v-if="isImageDocument" class="approval-document-preview mb-3">
                      <img :src="approvalDocumentUrl" alt="Approval document preview" class="approval-document-image" />
                    </div>
                    <a :href="approvalDocumentUrl" target="_blank" rel="noopener noreferrer" class="btn btn-outline-primary btn-sm">
                      View Document
                    </a>
                  </div>
                  <div v-else class="text-muted small py-2">Document unavailable.</div>
                </div>


                <!-- Warrant Details Card -->
                <div v-if="warrantData" class="mb-4">
                  <div class="warrant-details-card">
                    <div class="warrant-details-header d-flex justify-content-between align-items-center mb-3">
                      <h5 class="mb-0 fw-semibold">Generated Warrant</h5>
                      <button
                        type="button"
                        class="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                        @click="downloadWarrant(warrantData.id)"
                        :disabled="loadingDownloadWarrant"
                      >
                        <span v-if="loadingDownloadWarrant" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        {{ loadingDownloadWarrant ? 'Downloading...' : 'Download Warrant PDF' }}
                      </button>
                    </div>
                    <div class="row g-3">
                      <div class="col-sm-6">
                        <div class="warrant-field">
                          <span class="warrant-label">Warrant Number</span>
                          <span class="warrant-value">{{ warrantData.warrantNumber || '-' }}</span>
                        </div>
                      </div>
                      <div class="col-sm-6">
                        <div class="warrant-field">
                          <span class="warrant-label">Issue Date</span>
                          <span class="warrant-value">{{ warrantData.issueDate || '-' }}</span>
                        </div>
                      </div>
                      <div class="col-sm-6">
                        <div class="warrant-field">
                          <span class="warrant-label">Reference Number</span>
                          <span class="warrant-value">{{ warrantData.refNumber || '-' }}</span>
                        </div>
                      </div>
                      <div class="col-sm-6">
                        <div class="warrant-field">
                          <span class="warrant-label">MDA</span>
                          <span class="warrant-value">{{ warrantData.mdaName || '-' }}</span>
                        </div>
                      </div>
                      <div class="col-sm-6">
                        <div class="warrant-field">
                          <span class="warrant-label">Org Code</span>
                          <span class="warrant-value">{{ warrantData.orgCode || '-' }}</span>
                        </div>
                      </div>
                      <div class="col-sm-6">
                        <div class="warrant-field">
                          <span class="warrant-label">Amount Released (₦)</span>
                          <span class="warrant-value">{{ formatCurrency(warrantData.amountReleased || 0) }}</span>
                        </div>
                      </div>
                      <div class="col-sm-6">
                        <div class="warrant-field">
                          <span class="warrant-label">Nature of Expenditure</span>
                          <span class="warrant-value">{{ warrantData.natureOfExpenditure || '-' }}</span>
                        </div>
                      </div>
                      <div class="col-sm-6">
                        <div class="warrant-field">
                          <span class="warrant-label">Budget Year</span>
                          <span class="warrant-value">{{ warrantData.budgetYear || '-' }}</span>
                        </div>
                      </div>
                      <div v-if="warrantData.details" class="col-12">
                        <div class="warrant-field">
                          <span class="warrant-label">Details</span>
                          <span class="warrant-value">{{ warrantData.details }}</span>
                        </div>
                      </div>
                      <div v-if="warrantLineItems.length" class="col-12">
                        <div class="warrant-field">
                          <span class="warrant-label">Economic Line Items (ELI)</span>
                          <div class="table-responsive mt-2">
                            <table class="table table-sm table-bordered align-middle mb-0">
                              <thead class="table-light">
                                <tr>
                                  <th class="text-center">Eco Code</th>
                                  <th>Title</th>
                                  <th class="text-end">Amount Approved (₦)</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="(item, index) in warrantLineItems" :key="`warrant-eli-${item.id || item.mdaLineItemBudgetId || index}`">
                                  <td class="text-center">{{ item.ecoCode || item.economicCode || '-' }}</td>
                                  <td>{{ item.title || '-' }}</td>
                                  <td class="text-end">{{ formatCurrency(item.amountApproved || item.amountRequired || item.amtRequired || 0) }}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                </div><!-- /form-body -->

                <!-- Action Buttons -->
                <div class="mt-4 px-4 pb-3">
                  <div class="d-flex justify-content-between align-items-center">
                    <router-link :to="{ name: 'ViewSpecialApprovals' }" class="btn btn-outline-secondary">Back to list</router-link>
                    <div class="d-flex gap-2">
                      <button v-if="canEditDecision" type="button" class="btn btn-warning" @click="handleReturn" :disabled="savingReturn || savingApprove">
                        <span v-if="savingReturn" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {{ savingReturn ? 'Processing...' : 'Return' }}
                      </button>
                      <button v-if="canEditDecision" type="button" class="btn btn-success" @click="handleApprove" :disabled="savingApprove || savingReturn">
                        <span v-if="savingApprove" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {{ savingApprove ? 'Processing...' : 'Approve' }}
                      </button>
                      <button v-if="canEditReturned" type="button" class="btn btn-primary" @click="handleResubmit" :disabled="savingResubmit || ecoLoading">
                        <span v-if="savingResubmit" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {{ savingResubmit ? 'Saving...' : 'Save & Resubmit' }}
                      </button>
                      <button
                        v-if="canGenerateWarrant && !warrantData"
                        type="button"
                        class="btn btn-outline-primary"
                        @click="generateWarrant"
                        :disabled="warrantModalLoading"
                      >
                        <span v-if="warrantModalLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {{ warrantModalLoading ? 'Loading...' : 'Generate Warrant' }}
                      </button>
                      <button
                        v-if="warrantData"
                        type="button"
                        class="btn btn-primary"
                        @click="downloadWarrant(warrantData.id)"
                        :disabled="loadingDownloadWarrant"
                      >
                        <span v-if="loadingDownloadWarrant" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {{ loadingDownloadWarrant ? 'Downloading...' : 'Download Warrant' }}
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Warrant Generation Modal -->
    <div
      v-if="showWarrantModal"
      class="modal d-block"
      tabindex="-1"
      style="background: rgba(0,0,0,0.5); position: fixed; inset: 0; z-index: 1050;"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title fw-semibold">Generate Warrant</h5>
            <button type="button" class="btn-close" @click="showWarrantModal = false" :disabled="warrantSubmitting"></button>
          </div>
          <div class="modal-body">
            <div v-if="warrantModalLoading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
              <p class="mt-2 mb-0 text-muted">Loading MDA list...</p>
            </div>
            <div v-else class="row g-3">
              <div class="col-md-6">
                <label class="form-label fw-semibold">Originating MDA <span class="text-danger">*</span></label>
                <select class="form-select" v-model="warrantForm.mdaId" @change="onWarrantMdaChange">
                  <option value="" disabled>- Select MDA -</option>
                  <option v-for="mda in mdas" :key="mda.id" :value="mda.id">{{ mda.name }}</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Org Code</label>
                <input type="text" class="form-control form-view-only-input" :value="warrantForm.orgCode || '—'" readonly />
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Quarter <span class="text-danger">*</span></label>
                <select class="form-select" v-model="warrantForm.quarter">
                  <option value="" disabled>- Select Quarter -</option>
                  <option value="1">Q1</option>
                  <option value="2">Q2</option>
                  <option value="3">Q3</option>
                  <option value="4">Q4</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">DOB Name</label>
                <input type="text" class="form-control form-view-only-input" :value="warrantForm.dobName || '—'" readonly />
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Permanent Secretary</label>
                <input type="text" class="form-control form-view-only-input" :value="warrantForm.permSecName || '—'" readonly />
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Commissioner</label>
                <input type="text" class="form-control form-view-only-input" :value="warrantForm.commissionerName || '—'" readonly />
              </div>
              <div v-if="warrantBudgetLoading" class="col-12 text-center py-2">
                <div class="spinner-border spinner-border-sm text-primary me-2" role="status" aria-hidden="true"></div>
                <span class="text-muted small">Fetching budget data...</span>
              </div>
              <template v-else-if="warrantForm.mdaId">
                <div class="col-md-6">
                  <label class="form-label fw-semibold">Approved Estimates (₦)</label>
                  <input type="text" class="form-control form-view-only-input" :value="formatCurrency(warrantForm.approvedEstimates)" readonly />
                </div>
                <div class="col-md-6">
                  <label class="form-label fw-semibold">Released To Date (₦)</label>
                  <input type="text" class="form-control form-view-only-input" :value="formatCurrency(warrantForm.actualExpenditureYTD)" readonly />
                </div>
                <div class="col-md-6">
                  <label class="form-label fw-semibold">Balance (₦)</label>
                  <input type="text" class="form-control form-view-only-input" :value="formatCurrency(warrantForm.balance)" readonly />
                </div>
                <div class="col-md-6">
                  <label class="form-label fw-semibold">Performance (%)</label>
                  <input type="text" class="form-control form-view-only-input" :value="warrantForm.performance != null ? warrantForm.performance + '%' : '—'" readonly />
                </div>
              </template>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showWarrantModal = false" :disabled="warrantSubmitting">Cancel</button>
            <button
              type="button"
              class="btn btn-primary"
              @click="submitWarrant"
              :disabled="warrantSubmitting || warrantBudgetLoading || warrantModalLoading"
            >
              <span v-if="warrantSubmitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ warrantSubmitting ? 'Generating...' : 'Generate' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import Swal from 'sweetalert2'
import { useRoute } from 'vue-router'
import { useUserStore } from '@/stores/user'
import DashboardLayout from '../../layout/DashboardLayout.vue'

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const route = useRoute()
const userStore = useUserStore()
const id = route.params.id

const currentYear = new Date().getFullYear()
const years = ref(Array.from({ length: currentYear - 2025 + 1 }, (_, i) => currentYear - i))

const isDataLoaded = ref(false)
const savingApprove = ref(false)
const savingReturn = ref(false)
const savingResubmit = ref(false)
const loadingDownloadWarrant = ref(false)
const loadingDocument = ref(false)
const approvalDocumentUrl = ref('')
const documentNotFound = ref(false)
const warrantData = ref(null)
const specApprovalTypeName = ref('')
const showWarrantModal = ref(false)
const mdas = ref([])
const warrantModalLoading = ref(false)
const warrantBudgetLoading = ref(false)
const warrantSubmitting = ref(false)

const warrantForm = reactive({
  mdaId: '',
  originatingMda: '',
  orgCode: '',
  groupTypeId: '',
  quarter: '',
  approvedEstimates: 0,
  actualExpenditureYTD: 0,
  balance: 0,
  performance: 0,
  dobName: '',
  permSecId: '',
  permSecName: '',
  commissionerId: '',
  commissionerName: '',
})

const form = reactive({
  budgetYear: '',
  fileApprovedDate: '',
  fileNumber: '',
  initiatorName: '',
  sectoralHeadName: '',
  purposeOfRequest: '',
  amtRequired: 0,
  amtApproved: 0,
  approvedInd: 0,
  returnedInd: 0,
  returnReason: '',
  approved: false,
  returned: false,
  approvedByPermSec: false,
  approvedByCBP: false,
  permSecApprovedInd: 0,
  commissionerApprovedInd: 0,
  documentContentType: '',
  natOfExpenditure: '',
  specApprovalTypeId: null,
  loneSpecApprovalInd: 0,
  parentApprovalInd: 0,
  parentApproval: false,
  lineItems: [],
  subApprovals: [],
})

const isCommissioner = computed(() => !!userStore.profile?.role?.commOfBAndP)
const isPermSec = computed(() => !!userStore.profile?.role?.perSec)
const isGroupMember = computed(() => !!userStore.profile?.role?.groupMember)
const isGroupHeadOrMember = computed(() => !!userStore.profile?.role?.groupHead || isGroupMember.value)
const canParticipate = computed(() => isCommissioner.value || isPermSec.value)
const isReturnedRecord = computed(() => Boolean(form.returnedInd || form.returned))

const hasRoleWorked = computed(() =>
  Boolean(
    form.approvedByPermSec ||
    form.approvedByCBP ||
    form.permSecApprovedInd ||
    form.commissionerApprovedInd ||
    form.approvedInd ||
    form.returnedInd ||
    form.approved ||
    form.returned
  )
)

const canEditDecision = computed(() => canParticipate.value && !hasRoleWorked.value)
const canEditReturned = computed(() => isReturnedRecord.value && isGroupHeadOrMember.value)
const isApprovedRecord = computed(() =>
  Boolean(
    form.approvedInd ||
    form.approved ||
    form.approvedByCBP ||
    form.permSecApprovedInd ||
    form.approvedByPermSec
  )
)
const canGenerateWarrant = computed(() => isGroupHeadOrMember.value && isApprovedRecord.value)
const hasApprovalDocument = computed(() => Boolean(form.documentContentType))
const isImageDocument = computed(() => String(form.documentContentType || '').startsWith('image/'))

// ── Eco picking (editable when returned) ─────────────────────────────────────
const ecoOptions = ref([])
const ecoLoading = ref(false)
const searchTerm = ref('')
const newEcoId = ref(null)
const selectedEco = ref(null)

const fetchEcoOptions = async () => {
  if (!form.budgetYear || !form.natOfExpenditure) return
  const token = Cookies.get('auth_token')
  ecoLoading.value = true
  ecoOptions.value = []
  try {
    const { data } = await axios.get(
      `${root_api_url}/eli/mda-eli-budget/all/${form.budgetYear}/${form.natOfExpenditure}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    const items = Array.isArray(data) ? data : (data ? [data] : [])
    ecoOptions.value = items.map(dto => ({
      id: dto.id,
      ecoCode: dto.economicCode,
      title: dto.title,
      amountBudgeted: dto.amountBudgeted,
      amountReleased: dto.amountReleased,
      balance: dto.balance,
      performance: dto.performance,
    }))
  } catch {
    ecoOptions.value = []
  } finally {
    ecoLoading.value = false
  }
}

watch(() => [form.budgetYear, form.natOfExpenditure], ([year, nature]) => {
  if (canEditReturned.value && year && nature) fetchEcoOptions()
})

const filteredEcoOptions = computed(() =>
  ecoOptions.value.filter(item =>
    [item.ecoCode, item.title].join(' ').toLowerCase().includes(searchTerm.value.toLowerCase())
  )
)

const onSelectEco = () => {
  selectedEco.value = ecoOptions.value.find(e => e.id === newEcoId.value) || null
}

const addEcoItem = () => {
  if (!selectedEco.value) return
  if (form.lineItems.some(i => (i.mdaLineItemBudgetId || i.id) === selectedEco.value.id)) return
  form.lineItems.push({
    mdaLineItemBudgetId: selectedEco.value.id,
    ecoCode: selectedEco.value.ecoCode,
    title: selectedEco.value.title,
    amountBudgeted: selectedEco.value.amountBudgeted,
    amountReleased: selectedEco.value.amountReleased,
    balance: selectedEco.value.balance,
    amountRequired: 0,
    amountApproved: 0,
    year: Number(form.budgetYear || 0),
    quarter: 0,
  })
  newEcoId.value = null
  selectedEco.value = null
  searchTerm.value = ''
}

const removeLineItem = (index) => {
  form.lineItems.splice(index, 1)
}

const getFilteredSubEcoOptions = (sub) =>
  ecoOptions.value.filter(item =>
    [item.ecoCode, item.title].join(' ').toLowerCase().includes((sub._searchTerm || '').toLowerCase())
  )

const addSubEcoItem = (sub) => {
  const opt = ecoOptions.value.find(e => e.id === sub._newEcoId)
  if (!opt) return
  if ((sub.lineItems || []).some(i => (i.mdaLineItemBudgetId || i.id) === opt.id)) return
  if (!sub.lineItems) sub.lineItems = []
  sub.lineItems.push({
    mdaLineItemBudgetId: opt.id,
    ecoCode: opt.ecoCode,
    title: opt.title,
    amountBudgeted: opt.amountBudgeted,
    amountReleased: opt.amountReleased,
    balance: opt.balance,
    amountRequired: 0,
    amountApproved: 0,
    year: Number(form.budgetYear || 0),
    quarter: 0,
  })
  sub._newEcoId = null
  sub._searchTerm = ''
}

const removeSubLineItem = (sub, index) => {
  sub.lineItems.splice(index, 1)
}

const warrantLineItems = computed(() => {
  if (Array.isArray(warrantData.value?.lineItems) && warrantData.value.lineItems.length) {
    return warrantData.value.lineItems
  }
  return Array.isArray(form.lineItems) ? form.lineItems : []
})

const statusLabel = computed(() => {
  if (form.returnedInd || form.returned) return 'Returned'
  if (form.approvedInd || form.approved || form.approvedByCBP) return 'Approved'
  if (form.approvedByPermSec || form.permSecApprovedInd) return 'Perm Sec Approved'
  return 'Pending'
})

const statusClass = computed(() => {
  if (form.returnedInd || form.returned) return 'status-returned'
  if (form.approvedInd || form.approved || form.approvedByCBP) return 'status-approved'
  if (form.approvedByPermSec || form.permSecApprovedInd) return 'status-permsec'
  return 'status-pending'
})

const formatDate = (val) => {
  if (!val) return 'N/A'
  const date = new Date(val)
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const formatAmount = (val) => {
  if (val === '' || val === null || val === undefined) return ''
  return Number(parseFloat(val).toFixed(2)).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const formatCurrency = (val) => {
  if (val === '' || val === null || val === undefined) return 'N/A'
  return '₦' + formatAmount(val)
}

const MAX_SAFE_RAW = Number.MAX_SAFE_INTEGER.toString()

const onAmountInput = (event, target, key) => {
  let raw = event.target.value.replace(/\D/g, '')
  if (raw === '') { target[key] = ''; event.target.value = ''; return }
  if (raw.length > MAX_SAFE_RAW.length || (raw.length === MAX_SAFE_RAW.length && raw > MAX_SAFE_RAW)) raw = MAX_SAFE_RAW
  const num = parseInt(raw, 10) / 100
  target[key] = Number(num.toFixed(2))
  event.target.value = formatAmount(target[key])
}

const onEcoAmountInput = (event, item, key) => onAmountInput(event, item, key)

const fromDateTimeLocalToIso = (value) => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

const revokeApprovalDocumentUrl = () => {
  if (approvalDocumentUrl.value) {
    window.URL.revokeObjectURL(approvalDocumentUrl.value)
    approvalDocumentUrl.value = ''
  }
}

const fetchApprovalDocument = async () => {
  if (!id || !hasApprovalDocument.value) {
    revokeApprovalDocumentUrl()
    return
  }
  const token = Cookies.get('auth_token')
  loadingDocument.value = true
  try {
    const response = await axios.get(`${root_api_url}/appraisal/special-approval/${id}/document`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    })
    revokeApprovalDocumentUrl()
    approvalDocumentUrl.value = window.URL.createObjectURL(response.data)
  } catch (error) {
    revokeApprovalDocumentUrl()
    if (error?.response?.status === 404) {
      documentNotFound.value = true
    } else {
      console.error('Failed to load approval document:', error)
    }
  } finally {
    loadingDocument.value = false
  }
}

const mapLineItemsPayload = (items) => (items || []).map((item) => ({
  ...item,
  amountRequired: Number(item.amountRequired || 0),
  amountRecommended: Number(item.amountRecommended || item.amountRequired || 0),
  amountApproved: Number(item.amountApproved || 0),
  amountBudgeted: Number(item.amountBudgeted || 0),
  amountReleased: Number(item.amountReleased || 0),
  balance: Number(item.balance || 0),
}))

const buildPayload = (overrides = {}) => ({
  ...form,
  fileApprovedDate: fromDateTimeLocalToIso(form.fileApprovedDate),
  returnedInd: Number(form.returnedInd || 0),
  approvedInd: Number(form.approvedInd || 0),
  permSecApprovedInd: Number(form.permSecApprovedInd || 0),
  commissionerApprovedInd: Number(form.commissionerApprovedInd || 0),
  lineItems: mapLineItemsPayload(form.lineItems),
  subApprovals: (form.subApprovals || []).map((sub) => ({
    ...sub,
    _searchTerm: undefined,
    _newEcoId: undefined,
    specApprovalTypeName: undefined,
    lineItems: mapLineItemsPayload(sub.lineItems),
  })),
  ...overrides,
})

const fetchSpecApprovalType = async (typeId) => {
  if (!typeId) return
  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/appraisal/spec-approval-type/${typeId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    specApprovalTypeName.value = data.name || ''
  } catch {
    specApprovalTypeName.value = ''
  }
}

const fetchSpecialApproval = async () => {
  const token = Cookies.get('auth_token')
  isDataLoaded.value = false
  warrantData.value = null
  specApprovalTypeName.value = ''
  try {
    const { data } = await axios.get(`${root_api_url}/appraisal/special-approval/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    Object.assign(form, {
      ...data,
      fileApprovedDate: data.fileApprovedDate ? data.fileApprovedDate.substring(0, 10) : '',
      lineItems: data.lineItems || [],
      subApprovals: (data.subApprovals || []).map(sub => ({ ...sub, _searchTerm: '', _newEcoId: null })),
      returnReason: data.returnReason || '',
      purposeOfRequest: data.purposeOfRequest || '',
    })
    if (data.specApprovalTypeId) {
      fetchSpecApprovalType(data.specApprovalTypeId)
    }
    form.subApprovals
      .filter(sub => sub.specApprovalTypeId)
      .forEach(async (sub) => {
        try {
          const token = Cookies.get('auth_token')
          const { data: typeData } = await axios.get(
            `${root_api_url}/appraisal/spec-approval-type/${sub.specApprovalTypeId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
          sub.specApprovalTypeName = typeData.name || ''
        } catch {
          sub.specApprovalTypeName = ''
        }
      })
    if (canEditReturned.value) {
      fetchEcoOptions()
    }
    if (hasApprovalDocument.value) {
      await fetchApprovalDocument()
    }
  } catch (error) {
    console.error('Failed to fetch special approval:', error)
  } finally {
    isDataLoaded.value = true
  }
}

const handleApprove = async () => {
  if (!canEditDecision.value) return

  if (!form.fileApprovedDate) {
    Swal.fire({ icon: 'warning', text: 'Please select file approved date.' })
    return
  }

  const token = Cookies.get('auth_token')
  savingApprove.value = true
  try {
    const approverName = `${userStore.profile?.firstName || ''} ${userStore.profile?.lastName || ''}`.trim()
    const roleFlags = isCommissioner.value
      ? { commissionerApprovedInd: 1, approvedInd: 1, returnedInd: 0, commissionerName: approverName }
      : { permSecApprovedInd: 1, approvedInd: 1, returnedInd: 0, permSecName: approverName }

    await axios.put(`${root_api_url}/appraisal/special-approval/${id}`, buildPayload(roleFlags), {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })

    await Swal.fire({ icon: 'success', text: 'Special approval approved successfully.' })
    await fetchSpecialApproval()
  } catch (error) {
    const message = error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to approve special approval.'
    Swal.fire({ icon: 'error', text: message })
  } finally {
    savingApprove.value = false
  }
}

const handleReturn = async () => {
  if (!canEditDecision.value) return

  const { value: reason } = await Swal.fire({
    title: 'Return Special Approval',
    input: 'textarea',
    inputLabel: 'Reason for return',
    inputValue: form.returnReason || '',
    inputPlaceholder: 'Type return reason here...',
    showCancelButton: true,
    confirmButtonText: 'Return',
    cancelButtonText: 'Cancel',
    preConfirm: (value) => {
      if (!value || !String(value).trim()) {
        Swal.showValidationMessage('Return reason is required.')
      }
      return String(value || '').trim()
    },
  }) 

  if (!reason) return

  form.returnReason = reason

  const token = Cookies.get('auth_token')
  savingReturn.value = true
  try {
    await axios.put(`${root_api_url}/appraisal/special-approval/${id}`, buildPayload({
      returnReason: form.returnReason,
      returnedInd: 1,
      approvedInd: 0,
      permSecApprovedInd: 0,
      commissionerApprovedInd: 0,
    }), {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })

    await Swal.fire({ icon: 'success', text: 'Special approval returned successfully.' })
    await fetchSpecialApproval()
  } catch (error) {
    const message = error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to return special approval.'
    Swal.fire({ icon: 'error', text: message })
  } finally {
    savingReturn.value = false
  }
}

const handleResubmit = async () => {
  if (!canEditReturned.value) return

  const token = Cookies.get('auth_token')
  savingResubmit.value = true

  try {
    await axios.put(`${root_api_url}/appraisal/special-approval/${id}`, buildPayload({
      returnedInd: 0,
      approvedInd: 0,
      permSecApprovedInd: 0,
      commissionerApprovedInd: 0,
      approvedByPermSec: false,
      approvedByCBP: false,
      approved: false,
      returned: false,
      returnReason: null,
    }), {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })

    await Swal.fire({ icon: 'success', text: 'Special approval resubmitted successfully.' })
    await fetchSpecialApproval()
  } catch (error) {
    const message = error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to resubmit special approval.'
    Swal.fire({ icon: 'error', text: message })
  } finally {
    savingResubmit.value = false
  }
}

const toWarrantFullName = (user) => `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
const toWarrantUserId = (user) => user?.userId || user?.id || user?.uuid || ''

const fetchMDAs = async () => {
  const groupTypeId = userStore.profile?.groupTypeId
  if (!groupTypeId) return
  const token = Cookies.get('auth_token')
  warrantModalLoading.value = true
  try {
    const { data } = await axios.get(`${root_api_url}/eli/mda/group/${groupTypeId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    const items = Array.isArray(data) ? data : (data ? [data] : [])
    mdas.value = items.map(mda => ({
      id: mda.id,
      name: mda.name,
      orgCode: mda.orgCode,
      groupTypeId: mda.groupTypeId,
    }))
  } catch {
    mdas.value = []
  } finally {
    warrantModalLoading.value = false
  }
}

const fetchWarrantRoleUser = async (roleType) => {
  const token = Cookies.get('auth_token')
  const idResponse = await axios.get(`${root_api_url}/auth/role/client/roleType/${roleType}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  return axios.get(`${root_api_url}/auth/user/client/role/${idResponse.data}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
}

const fetchWarrantApproverNames = async () => {
  await Promise.allSettled([
    (async () => {
      try {
        const response = await fetchWarrantRoleUser(1)
        warrantForm.dobName = toWarrantFullName(response.data)
      } catch {
        warrantForm.dobName = ''
      }
    })(),
    (async () => {
      try {
        const response = await fetchWarrantRoleUser(2)
        warrantForm.permSecName = toWarrantFullName(response.data)
        warrantForm.permSecId = toWarrantUserId(response.data)
      } catch {
        warrantForm.permSecName = ''
        warrantForm.permSecId = ''
      }
    })(),
    (async () => {
      try {
        const response = await fetchWarrantRoleUser(3)
        warrantForm.commissionerName = toWarrantFullName(response.data)
        warrantForm.commissionerId = toWarrantUserId(response.data)
      } catch {
        warrantForm.commissionerName = ''
        warrantForm.commissionerId = ''
      }
    })(),
  ])
}

const fetchWarrantBudget = async () => {
  if (!warrantForm.mdaId || !form.budgetYear) return
  const token = Cookies.get('auth_token')
  const natCode = (form.natOfExpenditure === 'Capital' || Number(form.natOfExpenditure) === 3) ? 3 : (form.natOfExpenditure === 'Recurrent' || Number(form.natOfExpenditure) === 2) ? 2 : (form.natOfExpenditure === 'Hybrid' || Number(form.natOfExpenditure) === 4) ? 4 : 0
  warrantBudgetLoading.value = true
  try {
    const { data } = await axios.get(
      `${root_api_url}/appraisal/appraisal/budget/${warrantForm.mdaId}/${form.budgetYear}/${natCode}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    warrantForm.approvedEstimates = Number(data.amountBudgeted || 0)
    warrantForm.actualExpenditureYTD = Number(data.amountReleased || 0)
    warrantForm.balance = Number(data.balance || 0)
    warrantForm.performance = Number(data.performance || 0)
  } catch {
    warrantForm.approvedEstimates = 0
    warrantForm.actualExpenditureYTD = 0
    warrantForm.balance = 0
    warrantForm.performance = 0
  } finally {
    warrantBudgetLoading.value = false
  }
}

const onWarrantMdaChange = async () => {
  const selected = mdas.value.find(m => m.id === warrantForm.mdaId)
  warrantForm.originatingMda = selected?.name || ''
  warrantForm.orgCode = selected?.orgCode || ''
  warrantForm.groupTypeId = selected?.groupTypeId || ''
  await Promise.all([
    fetchWarrantBudget(),
    fetchWarrantApproverNames(),
  ])
}

const generateWarrant = async () => {
  Object.assign(warrantForm, {
    mdaId: form.mdaId || '',
    originatingMda: form.originatingMda || '',
    orgCode: form.orgCode || '',
    groupTypeId: form.groupTypeId || '',
    quarter: form.quarter || '',
    approvedEstimates: form.approvedEstimates || 0,
    actualExpenditureYTD: form.actualExpenditureYTD || 0,
    balance: form.balance || 0,
    performance: form.performance || 0,
    dobName: form.dobName || '',
    permSecId: form.permSecId || '',
    permSecName: form.permSecName || '',
    commissionerId: form.commissionerId || '',
    commissionerName: form.commissionerName || '',
  })
  if (!mdas.value.length) {
    await fetchMDAs()
  }
  showWarrantModal.value = true
}

const submitWarrant = async () => {
  if (!warrantForm.mdaId) {
    Swal.fire({ icon: 'warning', text: 'Please select an MDA.' })
    return
  }
  if (!warrantForm.quarter) {
    Swal.fire({ icon: 'warning', text: 'Please select a quarter.' })
    return
  }

  const token = Cookies.get('auth_token')
  warrantSubmitting.value = true
  try {
    const { data } = await axios.post(
      `${root_api_url}/appraisal/special-approval/warrant/${id}`,
      { ...buildPayload(), ...warrantForm },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    )

    if (data?.errorMessage) {
      Swal.fire({ icon: 'error', text: data.errorMessage })
      return
    }

    warrantData.value = data
    showWarrantModal.value = false
  } catch (error) {
    const message = error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to generate warrant.'
    Swal.fire({ icon: 'error', text: message })
  } finally {
    warrantSubmitting.value = false
  }
}

const downloadWarrant = async (warrantId) => {
  if (!warrantId) return

  const token = Cookies.get('auth_token')
  loadingDownloadWarrant.value = true
  try {
    const response = await axios.get(`${root_api_url}/appraisal/warrant/pdf/2/${warrantId}`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    })

    let downloadName = 'warrant.pdf'
    const disposition = response.headers['content-disposition']
    if (disposition && disposition.includes('filename=')) {
      downloadName = disposition.split('filename=')[1].replace(/"/g, '')
    }

    const blob = new Blob([response.data], { type: response.data.type })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', downloadName)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    const message = error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to download warrant.'
    Swal.fire({ icon: 'error', text: message })
  } finally {
    loadingDownloadWarrant.value = false
  }
}

onMounted(async () => {
  document.title = 'View Special Approval'
  if (!userStore.profile) {
    await userStore.fetchProfile()
  }
  fetchSpecialApproval()
})

onBeforeUnmount(() => {
  revokeApprovalDocumentUrl()
})
</script>

<style scoped>
.memo-content-area {
  min-height: 280px;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.form-view-only-input {
  background-color: #f8f9fa !important;
  border: 1px solid #dee2e6;
  color: #212529;
  cursor: default;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 0.4rem;
  margin-bottom: 1rem;
}

.status-badge {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.3rem 0.9rem;
  border-radius: 999px;
}

.status-approved {
  background-color: #d1fae5;
  color: #065f46;
}

.status-pending {
  background-color: #fef9c3;
  color: #854d0e;
}

.status-returned {
  background-color: #fee2e2;
  color: #991b1b;
}

.status-permsec {
  background-color: #dbeafe;
  color: #1e40af;
}

.sub-approval-card {
  background-color: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 1.25rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.eco-picker-box {
  background-color:var(--table-bg-head);
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 0.875rem 1rem;
}

.row-even {
  background-color: #ffffff;
}

.row-odd {
  background-color:var(--table-bg-head);
}

.approval-document-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.approval-document-preview {
  width: 100%;
  max-width: 520px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0.75rem;
  background-color: #fff;
}

.approval-document-image {
  display: block;
  width: 100%;
  max-height: 420px;
  object-fit: contain;
  border-radius: 6px;
}

.warrant-details-card {
  border: 1px solid #dee2e6;
  border-top: 3px solid #667eea;
  border-radius: 8px;
  padding: 1.25rem 1.5rem;
  background-color: #fff;
}

.warrant-details-header {
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.75rem;
}

.warrant-field {
  display: flex;
  flex-direction: column;
}

.warrant-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.2rem;
}

.warrant-value {
  font-size: 0.95rem;
  color: #212529;
  font-weight: 500;
}
</style>
